import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './WizardDialogue.css';

// Dialogue types for different scenarios
export const DIALOGUE_TYPES = {
    WELCOME: 'welcome',
    BEFORE_LESSON: 'before_lesson',
    BEFORE_GAME: 'before_game',
    VICTORY: 'victory',
    DEFEAT: 'defeat',
    WORLD_COMPLETE: 'world_complete',
    PATH_MASTERY: 'path_mastery',
    HINT: 'hint'
};

// Wizard moods affect the wizard's appearance
const WIZARD_MOODS = {
    neutral: '🧙‍♂️',
    teaching: '🧙‍♂️',
    excited: '🧙‍♂️',
    proud: '🧙‍♂️',
    concerned: '🧙‍♂️',
    celebrating: '🎉'
};

// Player avatars
const PLAYER_AVATARS = {
    student: '🧑‍🎓',
    apprentice: '🧑‍💼',
    wizard: '🧙'
};

function WizardDialogue({
    isOpen,
    onClose,
    dialogueType = DIALOGUE_TYPES.WELCOME,
    messages = [],
    playerName = 'Apprentice',
    playerAvatar = 'student',
    wizardMood = 'neutral',
    onComplete,
    autoAdvance = false,
    autoAdvanceDelay = 3000
}) {
    const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
    const [displayedText, setDisplayedText] = useState('');
    const [isTyping, setIsTyping] = useState(true);
    const [showContinue, setShowContinue] = useState(false);

    const currentMessage = messages[currentMessageIndex];
    const isLastMessage = currentMessageIndex === messages.length - 1;

    // Typewriter effect
    useEffect(() => {
        if (!currentMessage || !isOpen) return;

        setDisplayedText('');
        setIsTyping(true);
        setShowContinue(false);

        const text = currentMessage.text;
        let charIndex = 0;

        const typeInterval = setInterval(() => {
            if (charIndex < text.length) {
                setDisplayedText(text.substring(0, charIndex + 1));
                charIndex++;
            } else {
                clearInterval(typeInterval);
                setIsTyping(false);
                setShowContinue(true);

                // Auto-advance if enabled
                if (autoAdvance && !isLastMessage) {
                    setTimeout(() => {
                        handleContinue();
                    }, autoAdvanceDelay);
                }
            }
        }, 30); // Typing speed

        return () => clearInterval(typeInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentMessageIndex, currentMessage, isOpen]);

    const handleContinue = useCallback(() => {
        if (isTyping) {
            // Skip typing animation
            setDisplayedText(currentMessage.text);
            setIsTyping(false);
            setShowContinue(true);
            return;
        }

        if (isLastMessage) {
            // Dialogue complete
            onComplete?.();
            onClose();
        } else {
            // Next message
            setCurrentMessageIndex(prev => prev + 1);
        }
    }, [isTyping, isLastMessage, currentMessage, onComplete, onClose]);

    const handleSkip = () => {
        onComplete?.();
        onClose();
    };

    // Reset when dialogue opens
    useEffect(() => {
        if (isOpen) {
            setCurrentMessageIndex(0);
            setDisplayedText('');
            setIsTyping(true);
            setShowContinue(false);
        }
    }, [isOpen]);

    if (!isOpen || !currentMessage) return null;

    const speakerIsWizard = currentMessage.speaker === 'wizard';
    const speakerName = speakerIsWizard ? 'Master Wizard' : playerName;
    const speakerAvatar = speakerIsWizard
        ? WIZARD_MOODS[wizardMood] || WIZARD_MOODS.neutral
        : PLAYER_AVATARS[playerAvatar] || PLAYER_AVATARS.student;

    return (
        <AnimatePresence>
            <motion.div
                className="wizard-dialogue-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={handleContinue}
            >
                <motion.div
                    className="wizard-dialogue-container"
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 100, opacity: 0 }}
                    transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                    onClick={e => e.stopPropagation()}
                >
                    {/* Skip button */}
                    <button className="dialogue-skip-btn" onClick={handleSkip}>
                        Skip
                    </button>

                    {/* Characters */}
                    <div className="dialogue-characters">
                        {/* Player (left side) */}
                        <motion.div
                            className={`dialogue-character player ${!speakerIsWizard ? 'speaking' : ''}`}
                            animate={{
                                scale: !speakerIsWizard ? 1.1 : 1,
                                y: !speakerIsWizard ? -5 : 0
                            }}
                        >
                            <div className="character-avatar">
                                {PLAYER_AVATARS[playerAvatar] || PLAYER_AVATARS.student}
                            </div>
                            <div className="character-name">{playerName}</div>
                        </motion.div>

                        {/* Wizard (right side) */}
                        <motion.div
                            className={`dialogue-character wizard ${speakerIsWizard ? 'speaking' : ''}`}
                            animate={{
                                scale: speakerIsWizard ? 1.1 : 1,
                                y: speakerIsWizard ? -5 : 0
                            }}
                        >
                            <div className="character-avatar wizard-avatar">
                                {WIZARD_MOODS[wizardMood] || WIZARD_MOODS.neutral}
                                {wizardMood === 'celebrating' && (
                                    <span className="celebration-sparkles">✨</span>
                                )}
                            </div>
                            <div className="character-name">Master Wizard</div>
                        </motion.div>
                    </div>

                    {/* Dialogue Box */}
                    <div className={`dialogue-box ${speakerIsWizard ? 'wizard-speaking' : 'player-speaking'}`}>
                        <div className="dialogue-speaker">
                            <span className="speaker-avatar">{speakerAvatar}</span>
                            <span className="speaker-name">{speakerName}</span>
                        </div>

                        <div className="dialogue-text">
                            {displayedText}
                            {isTyping && <span className="typing-cursor">|</span>}
                        </div>

                        <div className="dialogue-footer">
                            <div className="dialogue-progress">
                                {messages.map((_, idx) => (
                                    <span
                                        key={idx}
                                        className={`progress-dot ${idx === currentMessageIndex ? 'active' : ''} ${idx < currentMessageIndex ? 'completed' : ''}`}
                                    />
                                ))}
                            </div>

                            {showContinue && (
                                <motion.button
                                    className="dialogue-continue-btn"
                                    onClick={handleContinue}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    {isLastMessage ? 'Got it!' : 'Continue'} →
                                </motion.button>
                            )}
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

// Pre-built dialogue generators for common scenarios
export const generateWelcomeDialogue = (playerName, topic) => [
    { speaker: 'wizard', text: `Welcome, ${playerName}! I am the Master Wizard of this realm.` },
    { speaker: 'wizard', text: `I see you wish to learn about "${topic}". An excellent choice!` },
    { speaker: 'player', text: `Yes, I'm ready to begin my journey!` },
    { speaker: 'wizard', text: `Each world you conquer will bring you closer to mastery. Complete lessons to unlock games, and earn stars to prove your worth!` },
    { speaker: 'wizard', text: `When you master all worlds, you too shall become a wizard. Now, begin your adventure!` }
];

export const generateBeforeLessonDialogue = (lessonTitle) => [
    { speaker: 'wizard', text: `Ah, you've chosen to study "${lessonTitle}".` },
    { speaker: 'wizard', text: `Pay close attention, apprentice. The knowledge within will be tested!` },
    { speaker: 'player', text: `I'll do my best, Master Wizard!` }
];

export const generateBeforeGameDialogue = (gameType) => [
    { speaker: 'wizard', text: `The time has come to test your knowledge!` },
    { speaker: 'wizard', text: `This ${gameType} will challenge what you've learned. Earn 3 stars to prove true mastery!` },
    { speaker: 'player', text: `I'm ready for the challenge!` },
    { speaker: 'wizard', text: `Good luck, apprentice. May wisdom guide your choices!` }
];

export const generateVictoryDialogue = (stars) => {
    const starMessages = {
        1: `You've passed the trial, but there's room for improvement. Keep studying!`,
        2: `Well done! Two stars show real understanding. A bit more practice and you'll master this!`,
        3: `Magnificent! Three stars! You've achieved true mastery of this challenge!`
    };

    return [
        { speaker: 'wizard', text: `Victory is yours, apprentice!` },
        { speaker: 'wizard', text: starMessages[stars] || starMessages[1] },
        { speaker: 'player', text: stars === 3 ? `Thank you, Master Wizard! That felt great!` : `I'll keep practicing to earn more stars!` },
        { speaker: 'wizard', text: `The path to mastery continues. Onward to new challenges!` }
    ];
};

export const generateDefeatDialogue = () => [
    { speaker: 'wizard', text: `The trial has bested you this time, apprentice.` },
    { speaker: 'player', text: `I didn't make it...` },
    { speaker: 'wizard', text: `Do not despair! Every great wizard has faced defeat. Review your lessons and try again!` },
    { speaker: 'wizard', text: `Remember: failure is simply another step on the path to mastery.` }
];

export const generateWorldCompleteDialogue = (worldName, worldNumber) => [
    { speaker: 'wizard', text: `Incredible! You've conquered ${worldName}!` },
    { speaker: 'wizard', text: `World ${worldNumber} is now complete. Your knowledge grows stronger!` },
    { speaker: 'player', text: `I can feel myself getting wiser!` },
    { speaker: 'wizard', text: `Indeed you are, apprentice. New worlds await your exploration!` }
];

export const generatePathMasteryDialogue = (playerName, topic) => [
    { speaker: 'wizard', text: `${playerName}... you have done it.` },
    { speaker: 'wizard', text: `All worlds conquered. All challenges overcome. All knowledge absorbed.` },
    { speaker: 'player', text: `Does this mean...?` },
    { speaker: 'wizard', text: `Yes! You have achieved FULL MASTERY of "${topic}"!` },
    { speaker: 'wizard', text: `I hereby bestow upon you the title of WIZARD!` },
    { speaker: 'player', text: `I... I'm a wizard now!` },
    { speaker: 'wizard', text: `You have earned it through dedication and perseverance. Welcome, fellow wizard!` }
];

export default WizardDialogue;
